import { useEffect, useState, useCallback } from "react";
import { Gift, Sparkles, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

interface AdminGrant {
  id: string;
  amount: number;
  kind: string;
  note: string | null;
  created_at: string;
}

/**
 * Globally mounted. When the signed-in user has an unseen admin credit
 * grant (with optional note), shows a big celebratory modal. Once the user
 * dismisses it, an ack row is inserted so it never appears again for that
 * grant. Also reacts in realtime if an admin grants while the user is
 * online.
 */
export function AdminGrantModal() {
  const { user } = useAuth();
  const [grant, setGrant] = useState<AdminGrant | null>(null);
  const [busy, setBusy] = useState(false);

  const checkUnseen = useCallback(async () => {
    if (!user) return;
    // Pull recent admin grants (kind grant_topup or admin_revoke with admin_id in meta)
    const { data: txs } = await supabase
      .from("credit_transactions")
      .select("id, amount, kind, meta, created_at")
      .eq("user_id", user.id)
      .in("kind", ["grant_topup", "admin_revoke"])
      .order("created_at", { ascending: false })
      .limit(20);
    if (!txs || txs.length === 0) return;
    const adminTxs = txs.filter((t: any) => t.meta && t.meta.admin_id);
    if (adminTxs.length === 0) return;
    const ids = adminTxs.map((t: any) => t.id);
    const { data: acked } = await supabase
      .from("admin_grant_acks")
      .select("transaction_id")
      .in("transaction_id", ids);
    const ackedSet = new Set((acked ?? []).map((a: any) => a.transaction_id));
    const unseen = adminTxs.find((t: any) => !ackedSet.has(t.id));
    if (unseen) {
      setGrant({
        id: unseen.id,
        amount: unseen.amount,
        kind: unseen.kind,
        note: (unseen.meta as any)?.note ?? null,
        created_at: unseen.created_at,
      });
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user) {
      setGrant(null);
      return;
    }
    void checkUnseen();
  }, [user?.id, checkUnseen]);

  // Realtime: react instantly if admin grants while user is online.
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`admin_grant_${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "credit_transactions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const tx: any = payload.new;
          if (!tx?.meta?.admin_id) return;
          if (tx.kind !== "grant_topup" && tx.kind !== "admin_revoke") return;
          // Show this one immediately (don't overwrite if user is mid-dialog)
          setGrant((prev) =>
            prev
              ? prev
              : {
                  id: tx.id,
                  amount: tx.amount,
                  kind: tx.kind,
                  note: tx.meta?.note ?? null,
                  created_at: tx.created_at,
                },
          );
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const dismiss = async () => {
    if (!grant || !user || busy) return;
    setBusy(true);
    await supabase
      .from("admin_grant_acks")
      .insert({ user_id: user.id, transaction_id: grant.id });
    setGrant(null);
    setBusy(false);
    // After dismissing, check if there are more queued grants
    void checkUnseen();
  };

  if (!grant) return null;

  const isRevoke = grant.kind === "admin_revoke" || grant.amount < 0;
  const displayAmount = Math.abs(grant.amount);
  const sign = isRevoke ? "−" : "+";

  return (
    <Dialog open={!!grant} onOpenChange={(o) => { if (!o) void dismiss(); }}>
      <DialogContent className="sm:max-w-md text-center overflow-hidden">
        <div
          className={`-mx-6 -mt-6 px-6 pt-8 pb-6 ${
            isRevoke
              ? "bg-gradient-to-b from-destructive/15 to-transparent"
              : "bg-gradient-to-b from-primary/20 via-primary/5 to-transparent"
          }`}
        >
          <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ring-4 ${
              isRevoke
                ? "bg-destructive/15 ring-destructive/20 text-destructive"
                : "bg-primary/15 ring-primary/20 text-primary"
            }`}
          >
            {isRevoke ? <AlertTriangle className="h-8 w-8" /> : <Gift className="h-8 w-8" />}
          </div>
          <DialogTitle className="text-3xl font-bold tracking-tight">
            {sign}
            {displayAmount.toLocaleString()} credits
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm text-muted-foreground inline-flex items-center justify-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            {isRevoke ? "Adjustment from the NexaFX team" : "From the NexaFX team"}
          </DialogDescription>
        </div>

        {grant.note ? (
          <div className="mx-1 rounded-xl border border-border bg-muted/40 p-4 text-left">
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">
              Message
            </p>
            <p className="text-base leading-relaxed text-foreground whitespace-pre-wrap break-words">
              {grant.note}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground px-2">
            {isRevoke
              ? "Your balance was adjusted by an admin."
              : "Enjoy the credits — go put them to work."}
          </p>
        )}

        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          className={`mt-2 inline-flex w-full items-center justify-center rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
            isRevoke
              ? "bg-destructive text-destructive-foreground hover:brightness-110"
              : "bg-primary text-primary-foreground hover:brightness-110"
          }`}
        >
          {busy ? "…" : isRevoke ? "Got it" : "Sweet, thanks!"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
